import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, MenuItem, Chip, Tabs, Tab,
  Divider, Alert, CircularProgress, TablePagination
} from '@mui/material';
import { Download, PictureAsPdf, AccountBalance } from '@mui/icons-material';
import { salaryService } from '../services/api';
import jsPDF from 'jspdf';
import { usePagination } from '../hooks/usePagination';
import { StatCard } from './ui/StatCard';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount);
}

function generatePayslipPDF(payslip: any) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const col1 = margin;
  const col2 = pageWidth / 2 + 5;
  let y = 15;

  // Header background
  doc.setFillColor(25, 118, 210);
  doc.rect(0, 0, pageWidth, 32, 'F');

  // Company name
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(payslip.companyName || 'Mqbit Technologies', pageWidth / 2, 12, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('SALARY SLIP', pageWidth / 2, 20, { align: 'center' });

  doc.setFontSize(10);
  doc.text(`${MONTHS[payslip.month - 1]} ${payslip.year}`, pageWidth / 2, 27, { align: 'center' });

  y = 40;
  doc.setTextColor(0, 0, 0);

  // Employee details box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(248, 249, 250);
  doc.rect(margin, y, pageWidth - margin * 2, 28, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('EMPLOYEE DETAILS', margin + 3, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);

  const empLeft = [
    ['Employee Name', payslip.userName || ''],
    ['Employee Code', payslip.employeeCode || 'N/A'],
  ];
  const empRight = [
    ['Designation', payslip.designation || 'N/A'],
    ['Department', payslip.department || 'N/A'],
  ];

  empLeft.forEach(([label, value], i) => {
    const lineY = y + 13 + i * 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text(`${label}:`, col1 + 3, lineY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(value, col1 + 38, lineY);
  });

  empRight.forEach(([label, value], i) => {
    const lineY = y + 13 + i * 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text(`${label}:`, col2, lineY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(value, col2 + 30, lineY);
  });

  y += 35;

  // Attendance summary box
  doc.setFillColor(232, 244, 253);
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, y, pageWidth - margin * 2, 20, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('ATTENDANCE SUMMARY', margin + 3, y + 6);

  const attItems = [
    { label: 'Working Days', value: String(payslip.workingDays) },
    { label: 'Present Days', value: String(payslip.presentDays) },
    { label: 'Paid Leave', value: String(payslip.paidLeaveDays ?? 0) },
    { label: 'Absent Days', value: String(payslip.absentDays) },
    { label: 'LWP Days', value: String(payslip.lwpDays) },
  ];

  const cellWidth = (pageWidth - margin * 2) / attItems.length;
  attItems.forEach(({ label, value }, i) => {
    const cx = margin + i * cellWidth + cellWidth / 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(25, 118, 210);
    doc.text(value, cx, y + 13, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    doc.text(label, cx, y + 18, { align: 'center' });
  });

  y += 27;

  // Earnings & Deductions table header
  const halfWidth = (pageWidth - margin * 2 - 5) / 2;

  // Earnings header
  doc.setFillColor(25, 118, 210);
  doc.rect(margin, y, halfWidth, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('EARNINGS', margin + halfWidth / 2, y + 5.5, { align: 'center' });

  // Deductions header
  doc.setFillColor(211, 47, 47);
  doc.rect(margin + halfWidth + 5, y, halfWidth, 8, 'F');
  doc.text('DEDUCTIONS', margin + halfWidth + 5 + halfWidth / 2, y + 5.5, { align: 'center' });

  y += 8;

  const perDaySalary = Number(payslip.perDaySalary) || 0;
  const absentDeduction = perDaySalary * (payslip.absentDays || 0);
  const lwpDeduction = Number(payslip.lwpDeduction) - absentDeduction;

  const earnings = [
    ['Basic Salary', formatCurrency(payslip.monthlySalary)],
  ];
  const deductions = [
    ['Absent Deduction', formatCurrency(absentDeduction)],
    ['LWP Deduction', formatCurrency(Math.max(0, lwpDeduction))],
  ];

  const rowH = 8;
  const maxRows = Math.max(earnings.length, deductions.length);

  for (let i = 0; i < maxRows; i++) {
    const rowY = y + i * rowH;
    // Earnings row
    doc.setFillColor(i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245);
    doc.rect(margin, rowY, halfWidth, rowH, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.rect(margin, rowY, halfWidth, rowH, 'D');

    if (earnings[i]) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(earnings[i][0], margin + 3, rowY + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.text(earnings[i][1], margin + halfWidth - 3, rowY + 5.5, { align: 'right' });
    }

    // Deductions row
    doc.setFillColor(i % 2 === 0 ? 255 : 250, i % 2 === 0 ? 248 : 245, i % 2 === 0 ? 248 : 245);
    doc.rect(margin + halfWidth + 5, rowY, halfWidth, rowH, 'F');
    doc.rect(margin + halfWidth + 5, rowY, halfWidth, rowH, 'D');

    if (deductions[i]) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(deductions[i][0], margin + halfWidth + 8, rowY + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(211, 47, 47);
      doc.text(deductions[i][1], margin + halfWidth + 5 + halfWidth - 3, rowY + 5.5, { align: 'right' });
    }
  }

  y += maxRows * rowH;

  // Totals row
  const totalRowH = 9;
  doc.setFillColor(232, 245, 233);
  doc.rect(margin, y, halfWidth, totalRowH, 'F');
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, y, halfWidth, totalRowH, 'D');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(27, 94, 32);
  doc.text('Total Earnings', margin + 3, y + 6);
  doc.text(formatCurrency(payslip.monthlySalary), margin + halfWidth - 3, y + 6, { align: 'right' });

  doc.setFillColor(255, 235, 238);
  doc.rect(margin + halfWidth + 5, y, halfWidth, totalRowH, 'F');
  doc.rect(margin + halfWidth + 5, y, halfWidth, totalRowH, 'D');
  doc.setTextColor(183, 28, 28);
  doc.text('Total Deductions', margin + halfWidth + 8, y + 6);
  doc.text(formatCurrency(payslip.lwpDeduction), margin + halfWidth + 5 + halfWidth - 3, y + 6, { align: 'right' });

  y += totalRowH + 8;

  // Net salary box
  doc.setFillColor(25, 118, 210);
  doc.rect(margin, y, pageWidth - margin * 2, 16, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('NET SALARY', margin + 5, y + 10);
  doc.text(formatCurrency(payslip.netSalary), pageWidth - margin - 5, y + 10, { align: 'right' });

  y += 24;

  // Payment status
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const statusText = payslip.paymentStatus === 'Paid'
    ? `Payment Date: ${payslip.paymentDate ? new Date(payslip.paymentDate).toLocaleDateString('en-IN') : '-'}`
    : 'Status: Pending';
  doc.text(statusText, margin, y);

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 12;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, footerY - 4, pageWidth - margin, footerY - 4);
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text('This is a system-generated payslip and does not require a signature.', pageWidth / 2, footerY, { align: 'center' });

  const fileName = `Payslip_${MONTHS[payslip.month - 1]}_${payslip.year}.pdf`;
  doc.save(fileName);
}

const MyPayslips: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [payslips, setPayslips] = useState<any[]>([]);
  const [ctcData, setCtcData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (tab === 0) loadPayslips();
    else loadCTC();
  }, [tab, filterYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPayslips = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await salaryService.getMyPayslips(filterYear);
      setPayslips(data);
    } catch {
      setError('Failed to load payslips.');
    } finally {
      setLoading(false);
    }
  };

  const loadCTC = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await salaryService.getMyCTC(filterYear);
      setCtcData(data);
    } catch {
      setError('Failed to load CTC data.');
    } finally {
      setLoading(false);
    }
  };

  const { pagedRows: pagedPayslips, paginationProps: payslipsPaginationProps } = usePagination(payslips);
  const { pagedRows: pagedBreakdown, paginationProps: breakdownPaginationProps } = usePagination(ctcData?.monthlyBreakdown ?? []);

  const statusChip = (status: string) => (
    <Chip
      label={status}
      size="small"
      color={status === 'Paid' ? 'success' : 'warning'}
      variant="outlined"
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>My Payroll</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<PictureAsPdf fontSize="small" />} iconPosition="start" label="My Payslips" />
          <Tab icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Yearly CTC" />
        </Tabs>
        <TextField
          select
          label="Year"
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 100, ml: 'auto' }}
        >
          {yearOptions.map(y => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Payslips Tab */}
      {!loading && tab === 0 && (
        <>
          {payslips.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No payslips found for {filterYear}.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Payslips are generated by HR each month after salary processing.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Gross Salary</TableCell>
                    <TableCell align="right">Working Days</TableCell>
                    <TableCell align="right">Present</TableCell>
                    <TableCell align="right">Paid Leave</TableCell>
                    <TableCell align="right">Absent</TableCell>
                    <TableCell align="right">Deductions</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedPayslips.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {MONTHS[p.month - 1]} {p.year}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(p.monthlySalary)}</TableCell>
                      <TableCell align="right">{p.workingDays}</TableCell>
                      <TableCell align="right">{p.presentDays}</TableCell>
                      <TableCell align="right" sx={{ color: p.paidLeaveDays > 0 ? 'info.main' : 'inherit' }}>
                        {p.paidLeaveDays ?? 0}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.absentDays > 0 ? 'error.main' : 'inherit' }}>
                        {p.absentDays}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.lwpDeduction > 0 ? 'error.main' : 'inherit' }}>
                        {formatCurrency(p.lwpDeduction)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {formatCurrency(p.netSalary)}
                      </TableCell>
                      <TableCell align="center">{statusChip(p.paymentStatus)}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download fontSize="small" />}
                          onClick={() => generatePayslipPDF(p)}
                        >
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {payslips.length > 0 && <TablePagination {...payslipsPaginationProps} />}
            </TableContainer>
          )}
        </>
      )}

      {/* CTC Tab */}
      {!loading && tab === 1 && ctcData && (
        <Box>
          {/* CTC Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard
              tone="primary"
              label="Annual CTC"
              value={formatCurrency(ctcData.annualCTC)}
              caption={`${formatCurrency(ctcData.monthlySalary)} / month`}
            />
            <StatCard
              tone="success"
              label="YTD Gross Earned"
              value={formatCurrency(ctcData.ytdGross)}
              caption={`${ctcData.monthlyBreakdown?.length || 0} month(s) processed`}
            />
            <StatCard
              tone="error"
              label="YTD Deductions"
              value={formatCurrency(ctcData.ytdDeductions)}
              caption="Absences + LWP"
            />
            <StatCard
              tone="successDark"
              label="YTD Net Paid"
              value={formatCurrency(ctcData.ytdNet)}
              caption="After all deductions"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Monthly breakdown */}
          <Typography variant="h6" gutterBottom>Monthly Breakdown — {filterYear}</Typography>

          {(!ctcData.monthlyBreakdown || ctcData.monthlyBreakdown.length === 0) ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No payslips processed for {filterYear} yet.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell><strong>Month</strong></TableCell>
                    <TableCell align="right"><strong>Gross</strong></TableCell>
                    <TableCell align="right"><strong>Working Days</strong></TableCell>
                    <TableCell align="right"><strong>Present</strong></TableCell>
                    <TableCell align="right"><strong>Paid Leave</strong></TableCell>
                    <TableCell align="right"><strong>Absent</strong></TableCell>
                    <TableCell align="right"><strong>LWP Days</strong></TableCell>
                    <TableCell align="right"><strong>Deduction</strong></TableCell>
                    <TableCell align="right"><strong>Net Salary</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedBreakdown.map((m: any) => (
                    <TableRow key={m.month} hover>
                      <TableCell>{MONTHS[m.month - 1]}</TableCell>
                      <TableCell align="right">{formatCurrency(m.monthlySalary)}</TableCell>
                      <TableCell align="right">{m.workingDays}</TableCell>
                      <TableCell align="right">{m.presentDays}</TableCell>
                      <TableCell align="right" sx={{ color: m.paidLeaveDays > 0 ? 'info.main' : 'inherit' }}>
                        {m.paidLeaveDays ?? 0}
                      </TableCell>
                      <TableCell align="right" sx={{ color: m.absentDays > 0 ? 'error.main' : 'inherit' }}>
                        {m.absentDays}
                      </TableCell>
                      <TableCell align="right" sx={{ color: m.lwpDays > 0 ? 'error.main' : 'inherit' }}>
                        {m.lwpDays}
                      </TableCell>
                      <TableCell align="right" sx={{ color: m.lwpDeduction > 0 ? 'error.main' : 'inherit' }}>
                        {formatCurrency(m.lwpDeduction)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {formatCurrency(m.netSalary)}
                      </TableCell>
                      <TableCell align="center">{statusChip(m.paymentStatus)}</TableCell>
                    </TableRow>
                  ))}

                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(ctcData.ytdGross)}</TableCell>
                    <TableCell colSpan={5} />
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(ctcData.ytdDeductions)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>{formatCurrency(ctcData.ytdNet)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
              {ctcData.monthlyBreakdown.length > 0 && <TablePagination {...breakdownPaginationProps} />}
            </TableContainer>
          )}
        </Box>
      )}

      {!loading && tab === 1 && !ctcData && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No CTC data available for {filterYear}.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MyPayslips;
